import { graphql } from '@/utils/graphql';
import * as A from '@/api';

export type InsightMemoryKind = 'facts' | 'preferences' | 'sessions' | 'notes' | 'memory';

export type InsightMemoryTopic = { name: string; text: string };

// AgentCore's summary strategy writes a conversation summary as one or more
// <topic name="…">…</topic> blocks. Anything outside a topic is kept as an unnamed one.
const TOPIC = /<topic\s+name="([^"]*)"\s*>([\s\S]*?)<\/topic>/g;
const parseTopics = (text: string): InsightMemoryTopic[] | null => {
  const topics = [...text.matchAll(TOPIC)].map((m) => ({ name: m[1].trim(), text: m[2].trim() }));
  if (topics.length === 0) return null;
  const rest = text.replace(TOPIC, '').trim();
  return rest ? [...topics, { name: '', text: rest }] : topics;
};

type InsightMemoryInput =
  A.BeemAgentInsightMemoriesQuery['beemAgentInsightMemories']['memories'][number];

export type InsightMemoryPage = {
  // False when the agent has no Memory configured in this environment.
  enabled: boolean;
  memories: InsightMemory[];
  nextToken?: string;
};

/**
 * Something the insights agent remembers about the current user in the current workspace
 * (BEEM-1448): a fact, a preference, a conversation summary, or a note the user added. The agent
 * resolves the user from the request, so these calls can only ever reach the caller's own memories.
 */
export default class InsightMemory {
  readonly id: string;
  readonly text: string;
  readonly kind: InsightMemoryKind;
  readonly createdAt?: string;
  readonly userAuthored: boolean;
  // The summary's topics, when the text is a conversation summary; null for plain text.
  readonly topics: InsightMemoryTopic[] | null;

  constructor(input: InsightMemoryInput) {
    this.id = input.id;
    this.text = input.text;
    this.kind = input.kind as InsightMemoryKind;
    this.createdAt = input.createdAt;
    this.userAuthored = !!input.userAuthored;
    this.topics = parseTopics(input.text);
  }

  static async list(nextToken?: string): Promise<InsightMemoryPage> {
    const rs = await graphql('beemAgentInsightMemories', {
      query: { maxResults: 50, ...(nextToken ? { nextToken } : {}) },
    });
    return {
      enabled: rs.enabled,
      memories: rs.memories.map((m) => new InsightMemory(m)),
      nextToken: rs.nextToken,
    };
  }

  // Returns null when Memory is not enabled.
  static async create(text: string): Promise<InsightMemory | null> {
    const rs = await graphql('beemAgentManageInsightMemory', {
      query: { action: A.InsightMemoryAction.CREATE, text },
    });
    return rs.memory ? new InsightMemory(rs.memory) : null;
  }

  async update(text: string): Promise<InsightMemory | null> {
    const rs = await graphql('beemAgentManageInsightMemory', {
      query: { action: A.InsightMemoryAction.UPDATE, id: this.id, text },
    });
    return rs.memory ? new InsightMemory(rs.memory) : null;
  }

  async delete(): Promise<void> {
    await graphql('beemAgentManageInsightMemory', {
      query: { action: A.InsightMemoryAction.DELETE, id: this.id },
    });
  }

  // Forgets one conversation: its history and its summary. Runs in the background on the agent.
  static async forgetConversation(sessionId: string): Promise<void> {
    await graphql('beemAgentManageInsightMemory', {
      query: { action: A.InsightMemoryAction.FORGET_CONVERSATION, sessionId },
    });
  }

  // Forgets everything remembered about the user in this workspace. Runs in the background.
  static async forgetAll(): Promise<void> {
    await graphql('beemAgentManageInsightMemory', {
      query: { action: A.InsightMemoryAction.FORGET_ALL },
    });
  }
}
